import React, { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { Container } from "reactstrap";
import { Stack, Button } from "react-bootstrap";
import { Form, Spinner } from "reactstrap";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { ReportFilters } from "./components/ReportFilters";
import {
  useGroupReportMutation,
  useGroupReportQuery,
} from "../../../services/reports";
import { TableC } from "../../../CommonElements/TableC";
import { usePagination } from "../../../utils/hooks/usePagination";
import { ActionsFooter } from "@/Component/ActionsFooter";
import { useSystem } from "@/utils/hooks/useLocation";
import { SYSTEMS } from "@/utils/permissions";
const GroupReport = () => {
  const { t } = useTranslation();
  const [reportDataRes, setReportData] = useState(null);
  const [shouldExport, setShouldExport] = useState(false);
  const [exportPayload, setExportPayload] = useState(null);
  const reportData = reportDataRes?.data?.data || [];
  const meta = reportDataRes?.data?.meta;
  const tableRef = useRef(null);
  const {currentSystem}=useSystem();
  const directionId = currentSystem === SYSTEMS.MAKKAH.code? SYSTEMS.MAKKAH.id : SYSTEMS.MADINA.id;
  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      format: "json",
      flight_date_from: "",
      flight_date_to: "",
      group_number: "",
      flight_number: "",
      entry_port_id: "",
      direction_id: directionId,
      coming_from_id: "",
      airline_id: "",
      capacity: "",
      service_center_id: "",
      buses_count: "",
      status: "",
      orientation: "portrait",
    },
  });

  const format = watch("format");

  // Mutation for JSON format
  const { mutate: generateReport, isPending: isGenerating } =
    useGroupReportMutation();

  // Query for PDF/Excel export (only enabled when shouldExport is true)
  const {
    isFetching: isExporting,
    isSuccess: isExportSuccess,
    isError: isExportError,
    error: exportError,
  } = useGroupReportQuery(exportPayload, shouldExport);

  // Handle export success/error
  useEffect(() => {
    if (isExportSuccess) {
      toast.success(
        t("REPORT_EXPORTED_SUCCESSFULLY") || "Report exported successfully"
      );
      setShouldExport(false);
      setExportPayload(null);
    }
    if (isExportError) {
      console.error("Group report export error:", exportError);
      toast.error(
        exportError?.response?.data?.message ||
          t("REPORT_EXPORT_FAILED") ||
          "Failed to export report"
      );
      setShouldExport(false);
      setExportPayload(null);
    }
  }, [isExportSuccess, isExportError, exportError, t]);

  const isLoading = isGenerating || isExporting;

  // Fetch report data (used for both initial load and page changes)
  const fetchReportData = (data, pageNum = 1) => {
    if (data.format === "json") {
      generateReport(
        {
          ...data,
          page: pageNum,
          per_page: 1,
        },
        {
          onSuccess: (response) => {
            setReportData(response);
            setTimeout(() => {
              tableRef.current?.scrollIntoView({ behavior: "smooth" });
            }, 100);
          },
        }
      );
    }
  };

  // Pagination hook
  const {
    page,
    setPage,
    hasSearched,
    handleSearch,
    handleReset: resetPagination,
  } = usePagination(fetchReportData, 10);

  const onSubmit = (data) => {
    if (data.format === "json") {
      // Use pagination hook to handle search
      handleSearch(data);
    } else if (data.format === "pdf" || data.format === "excel") {
      // Use query for PDF/Excel export
      setExportPayload(data);
      setShouldExport(true);
    }
  };

  // Table columns configuration (same as CampaignReport)
  const columns = [
    {
      name: t("SERVICE_CENTER"),
      key: "service_center_name",
    },
    {
      name: t("GROUP_NUMBER"),
      key: "campaign_number",
    },
    {
      name: t("FLIGHT_NUMBER"),
      key: "flight_number",
    },
    {
      name: t("DATE"),
      key: "flight_date",
    },
    {
      name: t("ENTRY_PORT"),
      key: "entry_port_name",
    },
    {
      name: t("COMING_FROM"),
      key: "coming_from",
    },
    {
      name: t("DIRECTION"),
      key: "direction_name",
    },
    {
      name: t("ORGANIZER"),
      key: "organizer",
    },
    {
      name: t("PILGRIMS_COUNT"),
      key: "capacity",
    },
    {
      name: t("HOUSING_CONTRACT"),
      key: "contract_id",
    },
    {
      name: t("BUILDING_NAME"),
      key: "building_name",
    },
  ];

  const buttonText = {
    pdf: t("EXPORT_PDF"),
    excel: t("EXPORT_EXCEL"),
    json: t("VIEW_REPORTS"),
  };

  const handleReset = () => {
    reset();
    resetPagination();
    setReportData(null);
  };

  const isPdf = format === "pdf";
  return (
    <Stack gap={3}>
      <Container fluid>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <ReportFilters
            isPdf={isPdf}
            control={control}
            errors={errors}
            type="group"
          />
          <ActionsFooter>
            <Button
              variant="primary"
              type="submit"
              disabled={isLoading}
              className="mt-3"
            >
              {isLoading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  {t("LOADING") || "Loading..."}
                </>
              ) : (
                buttonText[format]
              )}
            </Button>
            <Button
              variant="secondary"
              type="button"
              onClick={handleReset}
              disabled={isLoading}
              className="mt-3"
            >
              {t("RESET") || "Reset"}
            </Button>
          </ActionsFooter>
        </Form>
      </Container>

      {/* Display results table when search has been performed or data is available */}
      {(hasSearched || reportData.length > 0) && format === "json" && (
        <TableC
          ref={tableRef}
          data={reportData}
          columns={columns}
          loading={isGenerating}
          emptyStateTitle={t("NO_DATA") || "No data"}
          emptyStateDescription={t("NO_DATA_DESCRIPTION") || "No data found"}
          pagination={{
            current: page,
            total: meta?.total ?? 0,
            pageSize: 10,
            onChange: (newPage) => {
              setPage(newPage);
            },
          }}
        />
      )}
    </Stack>
  );
};

export default GroupReport;
